import { defineStore } from 'pinia'
import { getWsUrl, SFU_CONFIG } from '../config'
import { SfuControlClient } from '../control/controlClient'
import { useUserStore } from '@/stores/userStore'
import { createDebugLogger } from '../../config/debugLog'

const sfuDebug = createDebugLogger('sfu')

export const useSfuCallControlStore = defineStore('sfuCallCtrl', {
  state: () => ({
    client: null,
    incoming: [], // { conversationId, mode, fromPeerId, at }
    unansweredTimeoutMs: 30000,
    campaignActiveById: {}, // campaignId -> boolean
    campaignPeerCountById: {}, // campaignId -> number
    roomStateAvailable: null, // null unknown, true available, false unavailable
    regs: { connected: false },
    current: { showStage: false, mode: 'campaign', campaignId: '', targetUid: '', targets: [] },
    lastPeerId: ''
  }),
  actions: {
    async init(peerId) {
      const pid = String(peerId || '').trim()
      if (!pid) return
      if (
        this.regs?.connected === true &&
        this.lastPeerId === pid &&
        this.client?.isOpen &&
        this.client.isOpen()
      ) {
        return
      }
      if (this._initPromise) {
        return this._initPromise
      }
      this._initPromise = (async () => {
        if (!this.client) {
          const ctrl = new SfuControlClient(getWsUrl, SFU_CONFIG.authToken)
          ctrl.on('incoming', (d) => this._onIncoming(d))
          ctrl.on('accept', (d) => this._onAccepted(d))
          ctrl.on('decline', (d) => this._onDeclined(d))
          ctrl.on('hangup', (d) => this._onHangup(d))
          ctrl.on('ring-missed', (d) => this._onRingMissed(d))
          ctrl.on('closed', () => {
            this.regs.connected = false
          })
          this.client = ctrl
        }
        try {
          await this.client.connect(pid)
        } catch (err) {
          this.regs.connected = false
          this.lastPeerId = pid
          sfuDebug('[SFU] ctrl connect failed', err?.message || err)
          return false
        }
        this.regs.connected = true
        this.roomStateAvailable = null
        this.lastPeerId = pid
        return true
      })()
      try {
        return await this._initPromise
      } finally {
        this._initPromise = null
      }
    },
    _roomStateUrl(conversationId) {
      const convId = String(conversationId || '').trim()
      if (!convId) return ''
      try {
        const resolvedWs = String(typeof getWsUrl === 'function' ? getWsUrl() : '').trim()
        const fallbackOrigin =
          typeof window !== 'undefined'
            ? `${window.location.protocol}//${window.location.host}`
            : 'http://localhost:3001'
        const u = new URL(resolvedWs || '/ws', fallbackOrigin)
        let protocol = u.protocol
        if (protocol === 'ws:') protocol = 'http:'
        if (protocol === 'wss:') protocol = 'https:'
        const origin = `${protocol}//${u.host}`
        return `${origin}/room-state?conversationId=${encodeURIComponent(convId)}`
      } catch {
        return ''
      }
    },
    async refreshCampaignCallActivity(campaignId) {
      const cid = String(campaignId || '').trim()
      if (!cid) return { active: false, peerCount: 0 }
      // If control channel is not connected, skip probing room-state.
      // This avoids noisy CORS/404 failures when the backend is not ready.
      if (!this.regs?.connected && !(this.client?.isOpen && this.client.isOpen())) {
        return { active: false, peerCount: 0, skipped: true }
      }
      if (this.roomStateAvailable === false) {
        return { active: false, peerCount: 0, skipped: true }
      }
      const conversationId = `campaign:${cid}`
      const url = this._roomStateUrl(conversationId)
      if (!url) return { active: false, peerCount: 0 }
      try {
        const res = await fetch(url, { method: 'GET' })
        if (!res.ok) throw new Error(`room-state ${res.status}`)
        const payload = await res.json().catch(() => ({}))
        const peerCount = Math.max(0, Number(payload?.peerCount || 0))
        const active = Boolean(payload?.active === true || peerCount > 0)
        this.roomStateAvailable = true
        this.campaignActiveById = {
          ...(this.campaignActiveById || {}),
          [cid]: active
        }
        this.campaignPeerCountById = {
          ...(this.campaignPeerCountById || {}),
          [cid]: peerCount
        }
        return { active, peerCount }
      } catch (err) {
        this.campaignActiveById = {
          ...(this.campaignActiveById || {}),
          [cid]: false
        }
        this.campaignPeerCountById = {
          ...(this.campaignPeerCountById || {}),
          [cid]: 0
        }
        const msg = String(err?.message || '')
        if (msg.includes('room-state 404') || msg.includes('Failed to fetch')) {
          this.roomStateAvailable = false
          sfuDebug('[SFU] room-state unavailable; disabling polling')
        }
        return {
          active: false,
          peerCount: 0,
          error: err?.message || String(err || 'room-state failed')
        }
      }
    },
    watchCampaignCallActivity(campaignId, { intervalMs = 6000 } = {}) {
      const cid = String(campaignId || '').trim()
      if (!cid) return () => {}
      this.stopCampaignCallActivityWatch(cid)
      this.refreshCampaignCallActivity(cid).catch(() => {})
      const interval = Math.max(3000, Number(intervalMs || 6000))
      const map = this._campaignActivityIntervals || (this._campaignActivityIntervals = new Map())
      const timer = setInterval(() => {
        this.refreshCampaignCallActivity(cid).catch(() => {})
      }, interval)
      map.set(cid, timer)
      return () => this.stopCampaignCallActivityWatch(cid)
    },
    stopCampaignCallActivityWatch(campaignId) {
      const cid = String(campaignId || '').trim()
      if (!cid) return
      const map = this._campaignActivityIntervals || (this._campaignActivityIntervals = new Map())
      const timer = map.get(cid)
      if (timer) {
        try {
          clearInterval(timer)
        } catch {}
      }
      map.delete(cid)
    },
    _stopAllCampaignCallActivityWatches() {
      const map = this._campaignActivityIntervals || (this._campaignActivityIntervals = new Map())
      Array.from(map.values()).forEach((timer) => {
        try {
          clearInterval(timer)
        } catch {}
      })
      map.clear()
    },
    _outgoingKey(conversationId, peerId) {
      const cid = String(conversationId || '').trim()
      const pid = String(peerId || '').trim()
      if (!cid || !pid) return ''
      return `${cid}::${pid}`
    },
    _incomingId(call = {}) {
      const existing = String(call?.id || '').trim()
      if (existing) return existing
      const cid = String(call?.conversationId || '').trim()
      const from = String(call?.fromPeerId || '').trim()
      return `inc:${cid}:${from}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`
    },
    _clearOutgoingTimeout({ toPeerId, conversationId }) {
      const key = this._outgoingKey(conversationId, toPeerId)
      if (!key) return
      const timers = this._outgoingTimers || (this._outgoingTimers = new Map())
      const timer = timers.get(key)
      if (timer) {
        try {
          clearTimeout(timer)
        } catch {}
      }
      timers.delete(key)
    },
    _clearOutgoingTimeoutsForPeer(peerId) {
      const pid = String(peerId || '').trim()
      if (!pid) return
      const timers = this._outgoingTimers || (this._outgoingTimers = new Map())
      Array.from(timers.keys()).forEach((key) => {
        if (!key.endsWith(`::${pid}`)) return
        const timer = timers.get(key)
        if (timer) {
          try {
            clearTimeout(timer)
          } catch {}
        }
        timers.delete(key)
      })
    },
    _clearOutgoingTimeoutsForConversation(conversationId) {
      const cid = String(conversationId || '').trim()
      if (!cid) return
      const timers = this._outgoingTimers || (this._outgoingTimers = new Map())
      Array.from(timers.keys()).forEach((key) => {
        if (!key.startsWith(`${cid}::`)) return
        const timer = timers.get(key)
        if (timer) {
          try {
            clearTimeout(timer)
          } catch {}
        }
        timers.delete(key)
      })
    },
    _clearAllOutgoingTimeouts() {
      const timers = this._outgoingTimers || (this._outgoingTimers = new Map())
      Array.from(timers.values()).forEach((timer) => {
        try {
          clearTimeout(timer)
        } catch {}
      })
      timers.clear()
    },
    _removeCampaignTarget({ targetPeerId, conversationId }) {
      const targetId = String(targetPeerId || '').trim()
      const cid = String(conversationId || '').trim()
      if (!targetId || !cid || !cid.startsWith('campaign:')) return
      const campaignId = cid.replace(/^campaign:/, '').trim()
      const currentCampaignId = String(this.current?.campaignId || '').trim()
      const sameCampaignOpen =
        this.current?.showStage === true &&
        String(this.current?.mode || '') === 'campaign' &&
        currentCampaignId === campaignId
      if (!sameCampaignOpen) return
      const targets = Array.isArray(this.current?.targets) ? this.current.targets : []
      if (!targets.includes(targetId)) return
      this.current.targets = targets.filter((id) => String(id || '').trim() !== targetId)
    },
    _scheduleOutgoingTimeout({ toPeerId, conversationId, mode }) {
      const targetId = String(toPeerId || '').trim()
      const cid = String(conversationId || '').trim()
      if (!targetId || !cid) return
      this._clearOutgoingTimeout({ toPeerId: targetId, conversationId: cid })
      const timers = this._outgoingTimers || (this._outgoingTimers = new Map())
      const timeoutMs = Math.max(5000, Number(this.unansweredTimeoutMs || 30000))
      const key = this._outgoingKey(cid, targetId)
      const timer = setTimeout(() => {
        this._clearOutgoingTimeout({ toPeerId: targetId, conversationId: cid })
        const resolvedMode = String(mode || '').trim().toLowerCase()
        const isCampaign = resolvedMode === 'campaign' || cid.startsWith('campaign:')
        if (isCampaign) {
          this._removeCampaignTarget({ targetPeerId: targetId, conversationId: cid })
        } else {
          const sameDirectOpen =
            this.current?.showStage === true &&
            String(this.current?.mode || '') === 'direct' &&
            String(this.current?.targetUid || '').trim() === targetId
          if (sameDirectOpen) this.current.showStage = false
        }
        try {
          sfuDebug('[SFU] ring timeout', {
            conversationId: cid,
            peerId: targetId,
            mode: isCampaign ? 'campaign' : 'direct'
          })
        } catch {}
      }, timeoutMs)
      if (key) timers.set(key, timer)
    },
    _clearIncomingTimeout(callId) {
      const id = String(callId || '').trim()
      if (!id) return
      const timers = this._incomingTimers || (this._incomingTimers = new Map())
      const timer = timers.get(id)
      if (timer) {
        try {
          clearTimeout(timer)
        } catch {}
      }
      timers.delete(id)
    },
    _scheduleIncomingTimeout(call = {}) {
      const id = this._incomingId(call)
      if (!id) return
      this._clearIncomingTimeout(id)
      const timeoutMs = Math.max(5000, Number(this.unansweredTimeoutMs || 30000))
      const timers = this._incomingTimers || (this._incomingTimers = new Map())
      const timer = setTimeout(() => {
        const removed = this.popIncoming(id)
        if (!removed) return
        const toPeerId = String(removed?.fromPeerId || '').trim()
        const conversationId = String(removed?.conversationId || '').trim()
        if (toPeerId && conversationId) {
          try {
            this.decline({ toPeerId, conversationId })
          } catch {}
        }
        try {
          sfuDebug('[SFU] incoming timeout', {
            conversationId,
            fromPeerId: toPeerId
          })
        } catch {}
      }, timeoutMs)
      timers.set(id, timer)
    },
    markPeerAnswered({ peerId, conversationId }) {
      const pid = String(peerId || '').trim()
      const cid = String(conversationId || '').trim()
      if (!pid || !cid) return
      this._clearOutgoingTimeout({ toPeerId: pid, conversationId: cid })
    },
    _onIncoming(payload = {}) {
      const conversationId = String(payload?.conversationId || '').trim()
      const modeRaw = String(payload?.mode || '').trim().toLowerCase()
      const mode =
        modeRaw === 'campaign' || (!modeRaw && conversationId.startsWith('campaign:'))
          ? 'campaign'
          : 'direct'
      const fromPeerId = String(
        payload?.fromPeerId || payload?.fromUid || payload?.fromUserId || payload?.from || ''
      ).trim()
      const metaInput = payload?.meta && typeof payload.meta === 'object' ? payload.meta : {}
      const meta = {
        ...metaInput,
        displayName:
          metaInput.displayName ||
          payload?.fromDisplayName ||
          payload?.displayName ||
          metaInput.name ||
          '',
        photoURL: metaInput.photoURL || payload?.fromPhotoURL || payload?.photoURL || ''
      }
      const incomingCall = {
        ...payload,
        id: this._incomingId({ conversationId, fromPeerId }),
        conversationId,
        mode,
        fromPeerId,
        meta,
        at: Date.now()
      }
      this.incoming = [
        incomingCall,
        ...this.incoming
      ]
      this._scheduleIncomingTimeout(incomingCall)
    },
    _onAccepted(payload = {}) {
      const fromPeerId = String(payload?.fromPeerId || '').trim()
      const conversationId = String(payload?.conversationId || '').trim()
      if (!fromPeerId || !conversationId) return
      this._clearOutgoingTimeout({ toPeerId: fromPeerId, conversationId })
    },
    _onDeclined(payload = {}) {
      const fromPeerId = String(payload?.fromPeerId || '').trim()
      const conversationId = String(payload?.conversationId || '').trim()
      if (!fromPeerId || !conversationId) return
      this._clearOutgoingTimeout({ toPeerId: fromPeerId, conversationId })
      this._removeCampaignTarget({ targetPeerId: fromPeerId, conversationId })
      this._removeIncomingByConversation({ conversationId, fromPeerId })
      this._maybeClosePassiveStageForConversation({ conversationId, fromPeerId })
    },
    _onRingMissed(payload = {}) {
      const toPeerId = String(payload?.toPeerId || '').trim()
      const conversationId = String(payload?.conversationId || '').trim()
      if (toPeerId && conversationId) {
        const isCampaign = conversationId.startsWith('campaign:')
        if (!isCampaign) {
          this._clearOutgoingTimeout({ toPeerId, conversationId })
        }
        return
      }
      if (toPeerId) {
        // Legacy server payload may omit conversationId.
        // Do not clear campaign targets here; keep presence windows visible and
        // let unanswered timeout handle cleanup.
      }
    },
    _matchesCurrentStageConversation({ conversationId, fromPeerId }) {
      const convId = String(conversationId || '').trim()
      const from = String(fromPeerId || '').trim()
      if (!convId) return false
      const current = this.current || {}
      if (current.showStage !== true) return false
      const mode = String(current.mode || '').trim().toLowerCase()

      if (convId.startsWith('campaign:')) {
        if (mode !== 'campaign') return false
        const currentCampaignId = String(current.campaignId || '').trim()
        return convId === `campaign:${currentCampaignId}`
      }

      if (mode !== 'direct') return false
      const targetId = String(current.targetUid || '').trim()
      if (!targetId) return false
      if (from && from === targetId) return true
      return convId.includes(`:${targetId}`)
    },
    _removeIncomingByConversation({ conversationId, fromPeerId }) {
      const convId = String(conversationId || '').trim()
      const from = String(fromPeerId || '').trim()
      if (!convId) return
      const incoming = Array.isArray(this.incoming) ? this.incoming : []
      if (!incoming.length) return
      const kept = []
      for (const call of incoming) {
        const sameConv = String(call?.conversationId || '').trim() === convId
        const sameFrom = !from || String(call?.fromPeerId || '').trim() === from
        if (sameConv && sameFrom) {
          this._clearIncomingTimeout(call?.id)
          continue
        }
        kept.push(call)
      }
      this.incoming = kept
    },
    _maybeClosePassiveStageForConversation({ conversationId, fromPeerId }) {
      if (!this._matchesCurrentStageConversation({ conversationId, fromPeerId })) return
      const current = this.current || {}
      const mode = String(current.mode || '').trim().toLowerCase()
      const hasTargets = Array.isArray(current.targets) && current.targets.length > 0
      // Do not close an outgoing campaign caller stage if it is still dialing others.
      if (mode === 'campaign' && hasTargets) return
      this.closeStage()
    },
    _onHangup(payload = {}) {
      const fromPeerId = String(payload?.fromPeerId || '').trim()
      const conversationId = String(payload?.conversationId || '').trim()
      if (!conversationId) return

      this._removeIncomingByConversation({ conversationId, fromPeerId })
      this._maybeClosePassiveStageForConversation({ conversationId, fromPeerId })
    },
    async ringDirect({ toPeerId, conversationId, fromPeerId }) {
      const userStore = useUserStore()
      const convId = String(conversationId || '').trim()
      const targetId = String(toPeerId || '').trim()
      const senderId = String(fromPeerId || this.lastPeerId || '').trim()
      const senderName = String(
        userStore?.profile?.displayName ||
          userStore?.user?.displayName ||
          userStore?.userName ||
          ''
      ).trim()
      const senderPhoto = String(
        userStore?.profile?.photoURL || userStore?.user?.photoURL || userStore?.userAvatar || ''
      ).trim()
      const isCampaign = convId.startsWith('campaign:')
      const mode = isCampaign ? 'campaign' : 'direct'
      const campaignId = isCampaign ? convId.replace(/^campaign:/, '').trim() : ''

      // Prevent launching a direct ring while already in a campaign call stage.
      try {
        const hasOpenStage = this.current?.showStage === true
        const currentMode = String(this.current?.mode || '').trim().toLowerCase()
        if (!isCampaign && hasOpenStage && currentMode === 'campaign') {
          sfuDebug('[SFU] ringDirect blocked', {
            reason: 'campaign-stage-open',
            toPeerId: targetId
          })
          return false
        }
      } catch {
        /* ignore */
      }

      // Avoid duplicate campaign ring notifications for already-targeted peers
      // while the same campaign stage is currently open.
      try {
        if (isCampaign && targetId) {
          const currentCampaignId = String(this.current?.campaignId || '').trim()
          const existingTargets = Array.isArray(this.current?.targets) ? this.current.targets : []
          const sameCampaignOpen =
            this.current?.showStage === true &&
            String(this.current?.mode || '') === 'campaign' &&
            currentCampaignId === campaignId
          const key = this._outgoingKey(convId, targetId)
          const timers = this._outgoingTimers || (this._outgoingTimers = new Map())
          const isStillRingingTarget = !!timers.get(key)
          if (sameCampaignOpen && existingTargets.includes(targetId) && isStillRingingTarget)
            return false
        }
      } catch {
        /* ignore */
      }

      // Ensure control socket connected before sending
      if (!this.client) await this.init(senderId)
      const ok = await this.client?.ensureConnected(this.lastPeerId || senderId)
      if (!ok) {
        try {
          await this.init(senderId)
        } catch {}
      }
      this.client?.ring({
        toPeerId: targetId,
        conversationId: convId,
        mode,
        fromPeerId: senderId,
        fromDisplayName: senderName,
        fromPhotoURL: senderPhoto
      })
      // Track intended peers for campaign mass-call so UI can render presence windows
      try {
        if (isCampaign) {
          const id = targetId
          if (id) {
            const list = Array.isArray(this.current.targets) ? this.current.targets : []
            if (!list.includes(id)) this.current.targets = [...list, id]
          }
        }
      } catch {
        /* ignore */
      }
      this._scheduleOutgoingTimeout({ toPeerId: targetId, conversationId: convId, mode })
      return true
    },
    accept({ toPeerId, conversationId }) {
      this.client?.accept({ toPeerId, conversationId })
    },
    decline({ toPeerId, conversationId }) {
      this.client?.decline({ toPeerId, conversationId })
    },
    hangup({ toPeerId, conversationId }) {
      this.client?.hangup({ toPeerId, conversationId })
    },
    popIncoming(callOrId = null) {
      if (!this.incoming.length) return null
      if (!callOrId) {
        const first = this.incoming[0] || null
        this.incoming = this.incoming.slice(1)
        this._clearIncomingTimeout(first?.id)
        return first
      }
      const incomingId =
        typeof callOrId === 'string' ? callOrId : String(callOrId?.id || '').trim()
      if (!incomingId) {
        const first = this.incoming[0] || null
        this.incoming = this.incoming.slice(1)
        this._clearIncomingTimeout(first?.id)
        return first
      }
      const idx = this.incoming.findIndex((row) => String(row?.id || '').trim() === incomingId)
      if (idx < 0) return null
      const row = this.incoming[idx]
      this.incoming = [...this.incoming.slice(0, idx), ...this.incoming.slice(idx + 1)]
      this._clearIncomingTimeout(row?.id)
      return row
    },
    openStageForCampaign(campaignId) {
      const nextCampaignId = String(campaignId || '')
      const sameCampaignOpen =
        this.current?.showStage === true &&
        String(this.current?.mode || '') === 'campaign' &&
        String(this.current?.campaignId || '') === nextCampaignId
      const prevTargets = Array.isArray(this.current?.targets) ? this.current.targets : []
      this.current = {
        showStage: true,
        mode: 'campaign',
        campaignId: nextCampaignId,
        targetUid: '',
        targets: sameCampaignOpen ? [...prevTargets] : []
      }
      if (nextCampaignId) {
        this.campaignActiveById = {
          ...(this.campaignActiveById || {}),
          [nextCampaignId]: true
        }
      }
    },
    resetCampaignDialState(campaignId) {
      const cid = String(campaignId || '').trim()
      if (!cid) return
      const conversationId = `campaign:${cid}`
      this._clearOutgoingTimeoutsForConversation(conversationId)
      const sameCampaign =
        String(this.current?.mode || '') === 'campaign' &&
        String(this.current?.campaignId || '').trim() === cid
      if (sameCampaign) {
        this.current.targets = []
      }
    },
    openStageForDirect(targetUid) {
      const hasOpenStage = this.current?.showStage === true
      const currentMode = String(this.current?.mode || '').trim().toLowerCase()
      if (hasOpenStage && currentMode === 'campaign') {
        return false
      }
      this.current = {
        showStage: true,
        mode: 'direct',
        campaignId: '',
        targetUid: String(targetUid || '')
      }
      return true
    },
    closeStage() {
      const prevMode = String(this.current?.mode || '').trim()
      const prevCampaignId = String(this.current?.campaignId || '').trim()
      this.current.showStage = false
      this.current.targets = []
      this._clearAllOutgoingTimeouts()
      if (prevMode === 'campaign' && prevCampaignId) {
        this.refreshCampaignCallActivity(prevCampaignId).catch(() => {})
      }
    }
  }
})

export default { useSfuCallControlStore }
